import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatSelectChange, MatCheckboxChange } from '@angular/material';
import { Business } from '../models/business.model';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { CountriesService } from '../../shared/services/countries.service';
import { Country } from '../models/country.model';
import { State } from '../models/state.model';
import { Address } from '../../shared/models/address.model';
import { formatPhoneString, undoPhoneFormat } from '../../util/phone';


@Component({
  selector: 'app-edit-header',
  templateUrl: './edit-overview.component.html',
  styleUrls: ['./edit-overview.component.scss'],
})

export class EditOverviewComponent implements OnInit {
  isLoading = true;
  businessPanelText = 'Expand Business Address Form';
  mailingPanelText = 'Expand Mailing Address Form';
  businessPanelIsActive = true;
  business: Business;
  formGroup: FormGroup;
  countries: Country[];
  postalCodePlaceHolder = '';
  states: State [] = new Array<State>();
  statesMailing: State [] = new Array<State>();

  constructor(private matDialogRef: MatDialogRef<EditOverviewComponent>,
              @Inject(MAT_DIALOG_DATA) data,
              private businessManagerService: BusinessManagerService,
              private countriesService: CountriesService) {
    this.business = data.business; // data object defined in parent;
  }

  async ngOnInit() {
    // Defining form controls and validation.
    if (!this.business.mailingAddress) {
      this.business.mailingAddress = new Address();
    }
    this.formGroup = new FormGroup({
      title: new FormControl(this.business.name, [Validators.required, Validators.maxLength(50)]),
      doBusAs: new FormControl(this.business.doBusAs, [Validators.maxLength(50)]),
      street1: new FormControl(this.business.address.street1, [Validators.required, Validators.maxLength(80)]),
      street2: new FormControl(this.business.address.street2, [Validators.maxLength(80)]),
      stateProvince: new FormControl(''),
      postalCode: new FormControl(this.business.address.postalCode),
      city: new FormControl(this.business.address.city, [Validators.required, Validators.maxLength(40)]),
      country: new FormControl('', [Validators.required]),
      addressSame: new FormControl(false),
      mailingStreet1: new FormControl(this.business.mailingAddress.street1, [Validators.maxLength(80)]),
      mailingStreet2: new FormControl(this.business.mailingAddress.street2, [Validators.maxLength(80)]),
      mailingStateProvince: new FormControl('', [Validators.maxLength(30)]),
      mailingPostalCode: new FormControl(this.business.mailingAddress.postalCode, [Validators.maxLength(9)]),
      mailingCity: new FormControl(this.business.mailingAddress.city, Validators.maxLength(40)),
      mailingCountry: new FormControl(this.business.mailingAddress.country),
      phone: new FormControl(formatPhoneString(this.business.phone), [Validators.pattern('^([(]\\d{3}[)]\\s\\d{3}[-]\\d{4})$')]),
      fax: new FormControl(formatPhoneString(this.business.fax), [Validators.pattern('^([(]\\d{3}[)]\\s\\d{3}[-]\\d{4})$')]),
      webAddress: new FormControl(this.business.webAddress, [Validators.maxLength(100)]),
      email: new FormControl(this.business.email, [Validators.maxLength(55), this.customEmailValidator]),
      locationOnField: new FormControl(this.business.locationOnField),
      isLocatedOnField: new FormControl(true),
      locationDesc: new FormControl(this.business.locationDesc, [Validators.maxLength(50)])
    });
    try {
      this.countries = await this.countriesService.getCountries();
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false});
    }

    if (this.business.address.country) {
      try {
        this.states = await this.countriesService.getStates(this.business.address.country);
        this.states.length ? this.formGroup.controls['stateProvince'].setValidators([Validators.required, Validators.maxLength(50)]) :
                      this.formGroup.controls['stateProvince'].setValidators([Validators.maxLength(30)]);
        if (this.business.mailingAddress && this.business.mailingAddress.country) {
          this.statesMailing = await this.countriesService.getStates(this.business.mailingAddress.country);
        }
      } catch (e) {
        this.close({cancelBtnClicked: false, success: false});
      }
    }
    this.isLoading = false;
    this.formGroup.patchValue({country: this.business.address.country || ''});
    this.formGroup.patchValue({mailingCountry: this.business.mailingAddress.country || ''});
    this.formGroup.patchValue({stateProvince: this.business.address.stateProvince || ''});
    this.formGroup.patchValue({mailingStateProvince: this.business.mailingAddress.stateProvince || ''});
    if (this._compareAddress()) {
      this.formGroup.patchValue({addressSame: true});
      this._changeMailingFieldsState(true);
    }
    if (!this.business.locationOnField) {
      this.formGroup.patchValue({isLocatedOnField: false, locationOnField: ''});
    }
    this._setPostalCodeValidation(this.business.address.country);
  }

  private customEmailValidator(control: AbstractControl): ValidationErrors {
    if (!control.value) {
      return null;
    }

    return Validators.email(control);
  }

  /**
   * triggered when value inside countries dropdown changes; event.value contains two digit country code.
   * @param {MatSelectChange} event
   * @return {Promise<void>}
   */
  async countryChange(event: MatSelectChange) {
    // if value property is empty return
    if (!event.value) { return; }
    if (event.source.id.toString() === '1') {
      await this._setAddressForm(event.value);
      if (event.value === this.business.address.country) {
        this.formGroup.patchValue({stateProvince: this.business.address.stateProvince || ''});
      }
    } else if (event.source.id.toString() === '2') {
      await this._setMailingAddressForm(event.value);
      if (event.value === this.business.mailingAddress.country) {
        this.formGroup.patchValue({mailingStateProvince: this.business.mailingAddress.stateProvince || ''});
      }
    }
  }

  /**
   *  returns true if {Business} address and mailing address are the same.
   * @return {boolean}
   * @private
   */
  _compareAddress(): boolean {
    return (
      this.business.address.street1 === this.business.mailingAddress.street1 &&
      this.business.address.street2 === this.business.mailingAddress.street2 &&
      this.business.address.city === this.business.mailingAddress.city &&
      this.business.address.stateProvince === this.business.mailingAddress.stateProvince &&
      this.business.address.country === this.business.mailingAddress.country
    );
  }

  /**
   * Function toggles mailing address fields in UI betweened disabled and enabled based on boolean flag passed in.
   * @param {boolean} disabled
   * @return {Promise<void>}
   * @private
   */
  async _changeMailingFieldsState(disabled: boolean) {
    if (disabled) {
      this.statesMailing = this.states;
      this.formGroup.controls['mailingStreet1'].reset({value: this.formGroup.controls['street1'].value, disabled: true});
      this.formGroup.controls['mailingStreet2'].reset({value: this.formGroup.controls['street2'].value, disabled: true});
      this.formGroup.controls['mailingStateProvince'].reset({value: this.formGroup.controls['stateProvince'].value, disabled: true});
      this.formGroup.controls['mailingPostalCode'].reset({value: this.formGroup.controls['postalCode'].value, disabled: true});
      this.formGroup.controls['mailingCity'].reset({value: this.formGroup.controls['city'].value, disabled: true});
      this.formGroup.controls['mailingCountry'].reset({value: this.formGroup.controls['country'].value, disabled: true});
    } else {
      try {
        this.statesMailing = await this.countriesService.getStates(this.business.mailingAddress.country);
      } catch (e) {
        this.close({cancelBtnClicked: false, success: false});
      }
      this.formGroup.controls['mailingStreet1'].reset({value: this.business.mailingAddress.street1, disabled: false});
      this.formGroup.controls['mailingStreet2'].reset({value: this.business.mailingAddress.street2, disabled: false});
      this.formGroup.controls['mailingStateProvince'].reset({value: this.business.mailingAddress.stateProvince, disabled: false});
      this.formGroup.controls['mailingPostalCode'].reset({value: this.business.mailingAddress.postalCode, disabled: false});
      this.formGroup.controls['mailingCity'].reset({value: this.business.mailingAddress.city, disabled: false});
      this.formGroup.controls['mailingCountry'].reset({value: this.business.mailingAddress.country, disabled: false});

      this.formGroup.patchValue({
        mailingStreet1: this.business.mailingAddress.street1,
        mailingStreet2: this.business.mailingAddress.street2,
        mailingStateProvince: this.business.mailingAddress.stateProvince,
        mailingPostalCode: this.business.mailingAddress.postalCode,
        mailingCity: this.business.mailingAddress.city,
        mailingCountry: this.business.mailingAddress.country
      });
    }
  }

  /**
   * called on is same as business address mat checkbox change event.
   * @param {MatCheckboxChange} event
   */
  addressSameChange(event: MatCheckboxChange) {
    this._changeMailingFieldsState(event.source.checked);
  }

  /**
   * changes states dropdown when a different country is chosen.
   * also changes validators depending on whether or states list exists for country
   * @param {string} value
   * @return {Promise<void>}
   * @private
   */
  async _setAddressForm(value: string) {
    this.isLoading = true;
    try {
      this.states = await this.countriesService.getStates(value);
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false});
    }
    this.formGroup.patchValue({stateProvince: ''});
    this.isLoading = false;
    if (!this.states.length) {
      this.formGroup.controls['stateProvince'].setValidators([]);
    } else {
      this.formGroup.controls['stateProvince'].setValidators([Validators.required]);
    }
    this._setPostalCodeValidation(value);
  }

  async _setMailingAddressForm(value: string) {
    this.isLoading = true;
    try {
      this.statesMailing = await this.countriesService.getStates(value);
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false});
    }
    this.formGroup.patchValue({mailingStateProvince: ''});
    this.isLoading = false;
  }

  /**
   * If countryCode is 'US', Postal Code field becomes required.
   * @param {string} countryCode
   * @private
   */
  _setPostalCodeValidation(countryCode: string) {
    if (countryCode === 'US') {
      this.postalCodePlaceHolder = 'Zip Code *';
      this.formGroup.controls['postalCode'].setValidators([
        Validators.required,
        Validators.pattern('^\\d{5}$')
      ]);
      this.formGroup.controls['postalCode'].updateValueAndValidity();
    } else {
      this.postalCodePlaceHolder = 'Postal Code';
      this.formGroup.controls['postalCode'].setValidators([Validators.maxLength(9)]);
      this.formGroup.controls['postalCode'].updateValueAndValidity();
    }
  }

  businessPanelOpened() {
    this.businessPanelText = 'Enter Business Address';
    this.businessPanelIsActive = true;
  }

  businessPanelClosed() {
    this.businessPanelText = 'Expand Business Address Form';
  }

  mailingPanelOpened() {
    this.mailingPanelText = 'Enter Mailing Address';
    this.businessPanelIsActive = false;
    if (this.formGroup.controls['addressSame'].value) {
      this._changeMailingFieldsState(true);
    }
  }

  mailingPanelClosed() {
    this.mailingPanelText = 'Expand Mailing Address Form';
  }

  /**
   * formats passed in phone number string;
   * @param {string} field
   * @param {string} value
   * @return {string}
   */
  formatPhoneNumber (field: string, value: string): string {
    const NEW_NUMBER: string = formatPhoneString(value);
    this.formGroup.patchValue({[field]: NEW_NUMBER});
    return NEW_NUMBER;
  }

  /**
   * event triggered by keyup event on phone number or fax fields
   * @param event
   * @param {string} field
   * @param phoneInputObj
   */
  phoneFieldKeyUp (event, field: string, phoneInputObj) {
    const value: string = this.formGroup.get(field).value;
    const rawValue = undoPhoneFormat(value);
    const KEY = event.key;

    if (KEY === 'Backspace' && rawValue.length === 10 && phoneInputObj.selectionStart === rawValue.length) {
      this.formatPhoneNumber(field, value);
    } else if (phoneInputObj.selectionStart < value.length) {
    } else if (KEY === 'Backspace' || KEY === 'ArrowLeft' || KEY === 'ArrowRight') {
      return;
    } else {
      this.formatPhoneNumber(field, value);
    }
  }

  /**
   * triggered when phone number or fax field goes out of focus
   * @param {string} field
   */
  phoneFieldBlur(field: string) {
    const VALUE: string = this.formGroup.get(field).value;
    if (VALUE === '') {
      return;
    }
    this.formatPhoneNumber(field, VALUE);
  }


  /**
   * closes modal
   */
  close(result = {cancelBtnClicked: true, success: false}) {
    if (result) {
      this.matDialogRef.close(result);
    }
  }

  /**
   * submits form values to api server if formGroup.valid property is true
   * @return {Promise<void>}
   */
  async save() {
    const HAS_LOCATION = this.formGroup.value.isLocatedOnField && this.formGroup.value.locationOnField;
    const TEMP_BUSINESS = this.business;
    if (!this.formGroup.valid) {
      return;
    }

    if (this.formGroup.controls['addressSame'].value) {
      this._changeMailingFieldsState(true);
    }
    // form values are mapped to business object before api call.
    this.business.name = this.formGroup.value.title;
    this.business.doBusAs = this.formGroup.value.doBusAs;
    // business address
    this.business.address.street1 = this.formGroup.value.street1;
    this.business.address.street2 = this.formGroup.value.street2 || null;
    this.business.address.stateProvince = this.formGroup.value.stateProvince || null;
    this.business.address.postalCode = this.formGroup.value.postalCode || null;
    this.business.address.city = this.formGroup.value.city;
    this.business.address.country = this.formGroup.value.country || null;
    // mailing address
    this.business.mailingAddress.street1 = this.formGroup.getRawValue().mailingStreet1 || null;
    this.business.mailingAddress.street2 = this.formGroup.getRawValue().mailingStreet2 || null;
    this.business.mailingAddress.stateProvince = this.formGroup.getRawValue().mailingStateProvince || null;
    this.business.mailingAddress.postalCode = this.formGroup.getRawValue().mailingPostalCode || null;
    this.business.mailingAddress.city = this.formGroup.getRawValue().mailingCity || null;
    this.business.mailingAddress.country = this.formGroup.getRawValue().mailingCountry || null;
    // contact info & web address
    this.business.phone = undoPhoneFormat(this.formGroup.value.phone);
    this.business.fax = undoPhoneFormat(this.formGroup.value.fax);
    this.business.webAddress = this.formGroup.value.webAddress;
    this.business.email = this.formGroup.value.email;

    if (this.formGroup.value.locationDesc) {
      this.business.locationDesc = this.formGroup.value.locationDesc.trim();
    } else {
      this.business.locationDesc = null;
    }
    // handles checkbox field isLocatedOnField
    this.business.locationOnField = HAS_LOCATION ? this.formGroup.value.locationOnField : null;

    // make api call
    try {
      await this.businessManagerService.updateBusiness(this.business);
      this.close({cancelBtnClicked: false, success: true});
    } catch (e) {
      this.business = TEMP_BUSINESS;
      this.close({cancelBtnClicked: false, success: false});
    }
  }
}
